'use client';

import { useCallback, useMemo, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import type {
  CommandContext,
  WorkspaceClipboard,
} from '@/lib/workspace/commands';
import { detectPlatform } from '@/lib/workspace/commands';
import { useWorkspaceActions, useWorkspaceItems } from './store';
import {
  useWorkspaceSelection,
  type WorkspaceSelection,
} from './selection-context';

/**
 * Build a `CommandContext` factory. The factory returns a fresh
 * context object on every call so consumers always see the latest
 * selection / items snapshot — critical for the shortcut layer,
 * which evaluates predicates inside a long-lived `keydown` listener.
 *
 * We also expose a stable `cursor` ref (last pointermove over the
 * document) so commands like Paste land at the user's pointer
 * instead of an arbitrary canvas origin. Mirrors the cursor cache
 * inside `paste-handler.tsx`; the two could share storage in
 * future, but for now each surface keeps its own ref to avoid a
 * cross-cutting context dependency.
 */
export function useCommandContext(
  clipboard: WorkspaceClipboard | null,
  /** When the hook runs in `WorkspaceCanvas` (above the selection
   *  provider), pass the live selection snapshot explicitly. Child
   *  components inside the provider can omit this and read context. */
  selectionOverride?: WorkspaceSelection,
): () => CommandContext {
  const items = useWorkspaceItems();
  const contextSelection = useWorkspaceSelection();
  const selection = selectionOverride ?? contextSelection;
  const actions = useWorkspaceActions();
  const flow = useReactFlow();
  const platform = useMemo(() => detectPlatform(), []);

  // Cursor cache — last pointermove anywhere on the document, in
  // flow coordinates (so Paste / paste-as-link land precisely at
  // the pointer regardless of where the user invoked the menu).
  const cursorScreen = useRef<{ x: number; y: number } | null>(null);
  useMemo(() => {
    if (typeof window === 'undefined') return;
    const onMove = (e: PointerEvent) => {
      cursorScreen.current = { x: e.clientX, y: e.clientY };
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    // The `useMemo` cleanup pattern — return value is unused, but the
    // listener should live for the page lifetime. We intentionally
    // skip removal here because the host hook is mounted once at the
    // workspace canvas root.
    return () => document.removeEventListener('pointermove', onMove);
  }, []);

  // No camera facade is handed to commands (see `CommandContext` in
  // lib/workspace/commands.ts): `flow` is read here only to map the
  // cursor into flow coordinates.
  const itemById = useMemo(() => {
    const m = new Map<string, (typeof items)[number]>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  return useCallback((): CommandContext => {
    const cursorFlow = cursorScreen.current
      ? flow.screenToFlowPosition(cursorScreen.current)
      : null;
    return {
      selection: {
        nodeIds: selection.nodeIds,
        edgeIds: selection.edgeIds,
      },
      items,
      getItem: (id) => itemById.get(id),
      store: {
        addItem: actions.addItem,
        patchItemState: actions.patchItemState,
        removeItem: actions.removeItem,
        bringToFront: actions.bringToFront,
        bringForward: actions.bringForward,
        sendBackward: actions.sendBackward,
        sendToBack: actions.sendToBack,
      },
      cursor: cursorFlow,
      clipboard,
      platform,
    };
  }, [
    actions,
    clipboard,
    flow,
    items,
    itemById,
    platform,
    selection.edgeIds,
    selection.nodeIds,
  ]);
}
